import { all, takeLatest, put, call, select } from "redux-saga/effects";
import { toast } from 'react-hot-toast';
import usersAddEditSlice, { createUser, loadUser, loadUserOrg, loadUserRoles, selectUsersAddEditUserEmail, selectUsersAddEditUserRole, updateUser } from "./slice";
import { IConfiguration } from "../../../shared/types";
import { selectConfig } from "../../../shared/redux/slice";
import { selectIdToken, unauthorized } from "../../../auth/redux/slice";
import { SimplyTestApi } from "../../../api/simplytest";
import { AxiosResponse } from "axios";
import { OrganizationItem, UserItem, UserRole } from "../../../api/types";
import { UserFormState } from "../types";
import { parsePath } from "react-router-dom";
import { ROUTES } from "../../../shared/constants";
import { extractErrorMessage } from "../../../shared/utils";

// Username is always set to the same value as Email when creating/editing a user (see
// CreateUser.tsx's `email: username` destructure) - ASP.NET Identity checks each field's
// uniqueness separately, so one real "this email is already taken" problem comes back from
// AuthController.Register as two redundant-sounding ModelState entries (DuplicateEmail AND
// DuplicateUserName) for the same value. Collapse that specific pair into one clear message;
// any other validation error (PasswordTooShort, etc.) still falls through to extractErrorMessage.
function friendlyUserErrorMessage(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const errors = data as Record<string, unknown>;
    if (errors.DuplicateEmail || errors.DuplicateUserName)
        return 'An account with this email address already exists.';
    return undefined;
}

export abstract class UsersAddEditSagas {
    public static* run() {
        try {
            yield all([
                takeLatest(loadUser, UsersAddEditSagas.loadUser),
                takeLatest(createUser, UsersAddEditSagas.createUser),
                takeLatest(updateUser, UsersAddEditSagas.updateUser),
                takeLatest(loadUserOrg, UsersAddEditSagas.loadUserOrg),
                takeLatest(loadUserRoles, UsersAddEditSagas.loadUserRoles),
            ]);
        }
        catch(e: any){
            toast.error('Unable to register UsersAddEdit module.');
            console.log(`[UsersAddEditSagas:run] the following exception has occurred`, e);
        }
    }

    public static* loadUserRoles(action: ReturnType<typeof loadUserRoles>) {
        try {
            yield put(usersAddEditSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const getUserRolesResponse: AxiosResponse<UserRole[]> = yield call(
                {
                    context: client,
                    fn: client.getRoles,
                },
            );

            if(getUserRolesResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(getUserRolesResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: userRoles } = getUserRolesResponse;
            yield put(usersAddEditSlice.actions.setRoles(userRoles));
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Unable to load user roles.');
            console.log(`[UsersAddEditSagas:loadUserRoles] the following exception has occurred`, e);
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
    }

    public static* loadUserOrg(action: ReturnType<typeof loadUserOrg>){
        const { payload: { orgId }} = action;
        try {
            yield put(usersAddEditSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const getUserOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.getOrganization,
                },
                orgId!
            );

            if(getUserOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(getUserOrgResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: userOrg } = getUserOrgResponse;
            yield put(usersAddEditSlice.actions.setUserOrg(userOrg));
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Unable to load user organization info.');
            console.log(`[UsersAddEditSagas:loadUserOrg] the following exception has occurred`, e);
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
    }

    public static* loadUser(action: ReturnType<typeof loadUser>){
        const { payload: { userId }} = action;
        try {
            yield put(usersAddEditSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const getUserResponse: AxiosResponse<UserItem> = yield call(
                {
                    context: client,
                    fn: client.getUser,
                },
                userId
            );

            if(getUserResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(getUserResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: user } = getUserResponse;

            const getUserRolesResponse: AxiosResponse<string[]> = yield call(
                {
                    context: client,
                    fn: client.getUserRoles
                },
                user?.email
            );

            if(getUserRolesResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(getUserRolesResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: userRoles } = getUserRolesResponse;

            const getUserOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.getOrganization,
                },
                user.orgId!
            );

            if(getUserOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(getUserOrgResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: userOrg } = getUserOrgResponse;

            const userInfo: UserFormState = {
                ...user,
                roleName: userRoles?.length > 0 ? userRoles[0] : '',
            };

            console.log('userInfo', userInfo);
            yield put(usersAddEditSlice.actions.setUser(userInfo));
            yield put(usersAddEditSlice.actions.setUserOrg(userOrg));
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Unable to load user.');
            console.log(`[UsersAddEditSagas:loadUser] the following exception has occurred`, e);
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
    }

    public static* createUser(action: ReturnType<typeof createUser>){
        const { payload: { user, navigate, orgId }} = action;
        try {
            yield put(usersAddEditSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const createUserResponse: AxiosResponse<UserItem> = yield call(
                {
                    context: client,
                    fn: client.createUser,
                },
                user
            );

            if(createUserResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(createUserResponse.status !== 201)
            {
                // Identity validation failures (AuthController.Register) come back as a plain
                // ModelState dictionary - {"DuplicateEmail": ["Email 'x' is already taken."], ...}
                // - not a thrown exception, since the client is configured with
                // validateStatus: () => true. Surface those messages directly instead of a
                // generic failure toast that gives no clue why it failed.
                yield call({ context: toast, fn: toast.error }, friendlyUserErrorMessage(createUserResponse.data) ?? extractErrorMessage(createUserResponse.data, 'Unable to create user.'));
                yield put(usersAddEditSlice.actions.setState('READY'));
                return;
            }

            const { data: newUser } = createUserResponse;
            const { username } = newUser;
            const resetNewUserResponse: AxiosResponse<void> = yield call(
                {
                    context: client,
                    fn: client.resetPassword,
                },
                username
            );
            
            if(resetNewUserResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(usersAddEditSlice.actions.setState('READY'));
            yield call({ context: toast, fn: toast.success}, `New user created. A password reset link has been sent to ${username}.`);
            if(orgId)
            {
                navigate(parsePath(ROUTES.Organizations));
                return;
            }

            navigate(parsePath(ROUTES.Users));
        }
        catch(e: any){
            toast.error('Unable to create user.');
            console.log(`[UsersAddEditSagas:createUser] the following exception has occurred`, e);
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
    }

    public static* updateUser(action: ReturnType<typeof updateUser>){
        const { payload: { userId, user, navigate, orgId }} = action;
        try {
            yield put(usersAddEditSlice.actions.setState('BUSY'));
            const currentUserEmail: string | undefined = yield select(selectUsersAddEditUserEmail);
            const currentUserRole: string | undefined = yield select(selectUsersAddEditUserRole);
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            if(currentUserRole !== undefined && currentUserRole !== user.roleName){
                const removeCurrentRoleResponse: AxiosResponse<void> = yield call(
                    {
                        context: client,
                        fn: client.removeUserFromRole,
                    },
                    currentUserEmail,
                    currentUserRole
                );

                if(removeCurrentRoleResponse.status === 401)
                {
                    yield put(unauthorized(undefined));
                    return;
                }
                
                if(removeCurrentRoleResponse.status !== 200)
                    throw new Error("Error communicating with Api");

                const addUserToRoleResponse: AxiosResponse<void> = yield call(
                    {
                        context: client,
                        fn: client.addUserToRole,
                    },
                    currentUserEmail,
                    user.roleName,
                );

                if(addUserToRoleResponse.status === 401)
                {
                    yield put(unauthorized(undefined));
                    return;
                }
                
                if(addUserToRoleResponse.status !== 200)
                    throw new Error("Error communicating with Api");
            }


            const updateUserResponse: AxiosResponse<void> = yield call(
                {
                    context: client,
                    fn: client.updateUser,
                },
                userId,
                user
            );

            if(updateUserResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(updateUserResponse.status !== 200)
            {
                yield call({ context: toast, fn: toast.error }, friendlyUserErrorMessage(updateUserResponse.data) ?? extractErrorMessage(updateUserResponse.data, 'Unable to update user.'));
                yield put(usersAddEditSlice.actions.setState('READY'));
                return;
            }

            yield call({ context: toast, fn: toast.success}, `User Updated`);
            yield put(usersAddEditSlice.actions.setState('READY'));
            if(orgId)
            {
                navigate(parsePath(ROUTES.Organizations));
                return;
            }

            navigate(parsePath(ROUTES.Users));
        }
        catch(e: any){
            toast.error('Unable to update user.');
            console.log(`[UsersAddEditSagas:updateUser] the following exception has occurred`, e);
            yield put(usersAddEditSlice.actions.setState('READY'));
        }
    }
}